import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Important for cookies
});

export const authService = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
};

export const tenantService = {
  createTenant: (data) => api.post('/superadmin/tenants', data),
  listTenants: (params) => api.get('/superadmin/tenants', { params }),
  getTenant: (id) => api.get(`/superadmin/tenants/${id}`),
  updateTenant: (id, data) => api.put(`/superadmin/tenants/${id}`, data),
  updateUltraMsg: (id, data) => api.put(`/superadmin/tenants/${id}/ultramsg`, data),
  verifyUltraMsg: (id) => api.post(`/superadmin/tenants/${id}/ultramsg/verify`),
  updateOwners: (id, owners) => api.put(`/superadmin/tenants/${id}/owners`, owners),
  updateCommission: (id, data) => api.put(`/superadmin/tenants/${id}/commission`, data),
  // Respond.io config (SuperAdmin)
  updateRespondio: (id, data) => api.put(`/superadmin/tenants/${id}/respondio`, data),
  getRespondioStatus: (id) => api.get(`/superadmin/tenants/${id}/respondio/status`),
};

export const companyService = {
  getSettings: () => api.get('/company/settings'),
  updateSettings: (data) => api.put('/company/settings', data),
  getDebtorFields: () => api.get('/company/debtor-fields'),
  updateDebtorFields: (data) => api.put('/company/debtor-fields', data),
  addDebtorField: (data) => api.post('/company/debtor-fields', data),
  deleteDebtorField: (id) => api.delete(`/company/debtor-fields/${id}`),
};

export const bankAccountService = {
  list: () => api.get('/company/bank-accounts'),
  create: (data) => api.post('/company/bank-accounts', data),
  update: (id, data) => api.put(`/company/bank-accounts/${id}`, data),
  delete: (id) => api.delete(`/company/bank-accounts/${id}`),
};

export const teamService = {
  list: () => api.get('/company/team'),
  get: (id) => api.get(`/company/team/${id}`),
  create: (data) => api.post('/company/team', data),
  update: (id, data) => api.put(`/company/team/${id}`, data),
  delete: (id) => api.delete(`/company/team/${id}`),
};

export const portfolioService = {
  list: () => api.get('/company/portfolios'),
  get: (id) => api.get(`/company/portfolios/${id}`),
  create: (data) => api.post('/company/portfolios', data),
  update: (id, data) => api.put(`/company/portfolios/${id}`, data),
  delete: (id) => api.delete(`/company/portfolios/${id}`),
};

export const settlementService = {
  listAll: (month, year) => api.get('/superadmin/settlements', { params: { month, year } }),
  generate: (month, year) => api.post('/superadmin/settlements/generate', null, { params: { month, year } }),
  updateStatus: (id, status) => api.put(`/superadmin/settlements/${id}/status`, null, { params: { status } }),
};

export const analyticsService = {
  // SuperAdmin
  getConsultations: (days = 30, tenantId = null) => 
    api.get('/superadmin/analytics/consultations', { params: { days, tenant_id: tenantId } }),
  getSummary: () => api.get('/superadmin/analytics/summary'),
  // Company
  getCompanyConsultations: (days = 30, portfolioId = null) => 
    api.get('/company/analytics/consultations', { params: { days, portfolio_id: portfolioId } }),
  getCompanySummary: () => api.get('/company/analytics/summary'),
  // Debtor tracking
  trackAction: (token, action, section, metadata = null) => 
    api.post(`/debtor/${token}/track`, { action, section, metadata }),
};

export const respondioService = {
  getStatus: () => api.get('/company/respondio/status'),
  updateConfig: (data) => api.post('/company/settings/respondio', data),
  getTemplates: () => api.get('/company/respondio/templates'),
  sendTemplate: (data) => api.post('/company/respondio/send-template', data),
};

export const broadcastService = {
  list: (status = null) => api.get('/company/broadcasts', { params: status ? { status } : {} }),
  create: (data) => api.post('/company/broadcasts', data),
  get: (id) => api.get(`/company/broadcasts/${id}`),
  update: (id, data) => api.put(`/company/broadcasts/${id}`, data),
  delete: (id) => api.delete(`/company/broadcasts/${id}`),
  duplicate: (id) => api.post(`/company/broadcasts/${id}/duplicate`),
  executeNow: (id) => api.post(`/company/broadcasts/${id}/execute-now`),
  preview: (id, limit = 20) => api.get(`/company/broadcasts/${id}/preview`, { params: { limit } }),
  getStats: (id, executionIndex = -1) => api.get(`/company/broadcasts/${id}/stats`, { params: { execution_index: executionIndex } }),
  getSummaryStats: (days = 30) => api.get('/company/broadcasts/stats/summary', { params: { days } }),
  getCalendarEvents: (startDate, endDate) => api.get('/company/broadcasts/calendar/events', { params: { start_date: startDate, end_date: endDate } }),
};

export const campaignService = {
  list: (portfolioId = null) => api.get('/company/campaigns', { params: portfolioId ? { portfolio_id: portfolioId } : {} }),
  create: (data) => api.post('/company/campaigns', data),
  update: (id, data) => api.put(`/company/campaigns/${id}`, data),
  delete: (id) => api.delete(`/company/campaigns/${id}`),
  // Bulk import endpoints
  uploadFile: (campaignId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/company/campaigns/${campaignId}/upload-file`, formData);
  },
  executeImport: (importId, data) => api.post(`/company/campaigns/imports/${importId}/execute`, data),
  getImportStatus: (importId) => api.get(`/company/campaigns/imports/${importId}/status`),
  listImports: (campaignId) => api.get(`/company/campaigns/${campaignId}/imports`),
  downloadTemplate: () => api.get('/company/campaigns/download-template', { responseType: 'blob' }),
  exportCampaign: (campaignId) => {
    const portalBase = window.location.origin;
    return api.get(`/company/campaigns/${campaignId}/export?portal_base=${portalBase}`, { responseType: 'blob' });
  },
};

export const verificationService = {
  sendCode: (data) => api.post('/debtor/send-verification-code', data),
  verifyCode: (data) => api.post('/debtor/verify-code', data),
};

export const debtorService = {
  search: (data) => api.post('/debtor/search', data),
  getInfo: (token) => api.get(`/debtor/${token}`),
  getInvoices: (token) => api.get(`/debtor/${token}/invoices`),
  createDebtor: (data) => api.post('/company/debtors', data),
  updateDebtor: (debtorId, data) => api.put(`/company/debtors/${debtorId}`, data),
  delete: (debtorId) => api.delete(`/company/debtors/${debtorId}`),
  bulkDelete: (debtorIds) => api.post('/company/debtors/bulk-delete', { debtor_ids: debtorIds }),
  deleteAll: () => api.delete('/company/debtors/delete-all'),
  listDebtors: (params = {}) => api.get('/company/debtors', { params }),
  requestCertificate: (token) => api.post(`/debtor/${token}/request-certificate`),
  reportPayment: (token, file, comments, actaNumbers, contactEmail) => {
    const formData = new FormData();
    formData.append('proof_file', file);
    if (comments) formData.append('comments', comments);
    if (actaNumbers?.length) formData.append('acta_numbers', JSON.stringify(actaNumbers));
    if (contactEmail) formData.append('contact_email', contactEmail);
    return api.post(`/debtor/${token}/report-payment`, formData);
  },
  getRequests: (token) => api.get(`/debtor/${token}/requests`),
  uploadInvoice: (portfolioId, debtorId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/company/portfolios/${portfolioId}/debtors/${debtorId}/invoice`, formData);
  },
  createPayment: (token, amount, actaNumbers = []) => api.post(`/debtor/${token}/create-payment`, { amount, acta_numbers: actaNumbers }),
  getPaymentHistory: (token) => api.get(`/debtor/${token}/payments`),
};

export const invoiceService = {
  create: (data) => api.post('/company/invoices', data),
};

export const paymentService = {
  createPreference: (data) => api.post('/payments/create-preference', data),
  // Payment plans
  createPlan: (debtorId, data) => api.post(`/company/debtors/${debtorId}/payment-plan`, data),
  getPlans: (debtorId) => api.get(`/company/debtors/${debtorId}/payment-plans`),
  recordPlanPayment: (planId) => api.post(`/company/payment-plans/${planId}/record-payment`),
  // Payment history
  getDebtorPayments: (debtorId) => api.get(`/company/debtors/${debtorId}/payments`),
  recordPayment: (debtorId, amount, method, reference, notes) => 
    api.post(`/company/debtors/${debtorId}/record-payment?amount=${amount}&payment_method=${method}${reference ? `&reference=${reference}` : ''}${notes ? `&notes=${notes}` : ''}`),
};

export const metricsService = {
  getMetrics: () => api.get('/company/metrics'),
};

export const requestsService = {
  listRequests: () => api.get('/company/requests'),
  updateRequest: (requestId, data) => api.put(`/company/requests/${requestId}`, data),
};

export const documentRequestService = {
  list: (params) => api.get('/company/document-requests', { params }),
  get: (id) => api.get(`/company/document-requests/${id}`),
  create: (data) => api.post('/company/document-requests', data),
  update: (id, data) => api.put(`/company/document-requests/${id}`, null, { params: data }),
  upload: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/company/document-requests/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const massWhatsAppService = {
  send: (debtorIds, messageTemplate) => api.post('/company/mass-whatsapp', { debtor_ids: debtorIds, message_template: messageTemplate }),
};

export const exportService = {
  exportDebtors: (portfolioId) => {
    const portalBase = encodeURIComponent(window.location.origin);
    let url = `/company/debtors/export?portal_base=${portalBase}`;
    if (portfolioId) url += `&portfolio_id=${portfolioId}`;
    return api.get(url, { responseType: 'blob' });
  }
};

export const chatService = {
  sendMessage: (data) => api.post('/chat/send', data),
};

export const mercadoPagoService = {
  updateConfig: (data) => api.put('/company/mercadopago-config', data),
};

// API Keys & Webhooks
export const apiKeyService = {
  list: () => api.get('/company/api-keys'),
  create: (data) => api.post('/company/api-keys', data),
  update: (keyId, data) => api.put(`/company/api-keys/${keyId}`, data),
  revoke: (keyId) => api.delete(`/company/api-keys/${keyId}`),
};

export const actaZipService = {
  upload: (file, portfolioId) => {
    const fd = new FormData();
    fd.append('file', file);
    if (portfolioId) fd.append('portfolio_id', portfolioId);
    return api.post('/company/actas/zip-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  jobs: () => api.get('/company/actas/zip-jobs'),
  job: (id) => api.get(`/company/actas/zip-jobs/${id}`),
};

export const crmService = {
  settings: () => api.get('/company/crm/settings'),
  updateSettings: (data) => api.put('/company/crm/settings', data),
  metrics: (params = {}) => api.get('/company/crm/metrics', { params }),
  users: () => api.get('/company/crm/users'),
  debtor: (debtorId) => api.get(`/company/crm/debtors/${debtorId}`),
  addContact: (debtorId, data) => api.post(`/company/crm/debtors/${debtorId}/contacts`, data),
  updateContact: (debtorId, contactId, data) => api.patch(`/company/crm/debtors/${debtorId}/contacts/${encodeURIComponent(contactId)}`, data),
  deleteContact: (debtorId, contactId) => api.delete(`/company/crm/debtors/${debtorId}/contacts/${encodeURIComponent(contactId)}`),
  setState: (debtorId, data) => api.put(`/company/crm/debtors/${debtorId}/state`, data),
  createInteraction: (debtorId, data) => api.post(`/company/crm/debtors/${debtorId}/interactions`, data),
  tasks: (params = {}) => api.get('/company/crm/tasks', { params }),
  createTask: (data) => api.post('/company/crm/tasks', data),
  updateTask: (taskId, data) => api.patch(`/company/crm/tasks/${taskId}`, data),
};

export const webhookService = {
  list: () => api.get('/company/webhooks'),
  create: (data) => api.post('/company/webhooks', data),
  update: (webhookId, data) => api.put(`/company/webhooks/${webhookId}`, data),
  delete: (webhookId) => api.delete(`/company/webhooks/${webhookId}`),
  getEvents: () => api.get('/company/webhook-events'),
};

// Notifications
export const notificationService = {
  list: (params) => api.get('/company/notifications', { params }),
  getUnreadCount: () => api.get('/company/notifications/unread-count'),
  markRead: (id) => api.put(`/company/notifications/${id}/read`),
  markAllRead: () => api.put('/company/notifications/read-all'),
  getStreamUrl: () => `${process.env.REACT_APP_BACKEND_URL}/api/company/notifications/stream`,
};

// Tenant Switcher
export const tenantSwitcherService = {
  getMyTenants: () => api.get('/auth/my-tenants'),
  switchTenant: (tenantId) => api.post('/auth/switch-tenant', { tenant_id: tenantId }),
};

// Analytics - Recent Consultations
export const recentConsultationsService = {
  list: (params) => api.get('/company/analytics/recent-consultations', { params }),
};

// Cobro Recurrente
export const recurringService = {
  get: (debtorId) => api.get(`/recurring/debtor/${debtorId}`),
  update: (debtorId, data) => api.put(`/recurring/debtor/${debtorId}`, data),
  list: () => api.get('/recurring/list'),
  process: () => api.post('/recurring/process'),
};

// Descargos
export const descargoService = {
  getConfig: () => api.get('/company/descargos/config'),
  updateConfig: (data) => api.put('/company/descargos/config', data),
  list: (params) => api.get('/company/descargos', { params }),
  updateStatus: (id, data) => api.put(`/company/descargos/${id}/status`, data),
  getStats: () => api.get('/company/descargos/stats'),
  exportCsv: () => api.get('/company/descargos/export?format=csv', { responseType: 'blob' }),
  exportTxt: () => api.get('/company/descargos/export?format=txt', { responseType: 'blob' }),
  downloadAttachment: (id, params = '') => api.get(`/company/descargos/${id}/attachment${params}`, { responseType: 'blob' }),
};


// Veritel Phone Verification
export const veritelService = {
  verifySingle: (phone, check_whatsapp = true) => api.post('/company/veritel/verify', { phone, check_whatsapp }),
  verifyBulk: (check_whatsapp = true) => api.post('/company/veritel/verify-bulk', { check_whatsapp }),
  getStats: () => api.get('/company/veritel/stats'),
};


// Cobranza Automatizada
export const cobranzaService = {
  getWorkflows: () => api.get('/company/cobranza/workflows'),
  createWorkflow: (data) => api.post('/company/cobranza/workflows', data),
  updateWorkflow: (id, data) => api.put(`/company/cobranza/workflows/${id}`, data),
  deleteWorkflow: (id) => api.delete(`/company/cobranza/workflows/${id}`),
  getScheduler: () => api.get('/company/cobranza/scheduler'),
  updateScheduler: (data) => api.put('/company/cobranza/scheduler', data),
  execute: () => api.post('/company/cobranza/execute'),
  preview: (data) => api.post('/company/cobranza/preview', data),
  getLogs: (params) => api.get('/company/cobranza/logs', { params }),
  getStats: () => api.get('/company/cobranza/stats'),
};

